import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { GrossProfitApis } from 'apis/GrossProfitApis';
import { ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useDefaultFilterReward from 'hooks/useDefaultFilterReward';
import useQueryParam from 'hooks/useQueryPrams';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { IMPORT_GROSS_PROFIT } from 'utils/constants/importId';
import FilterGrossProfit from './FilterGrossProfit';
import TableGrossProfit from './TableGrossProfit';

const GrossProfitPage = () => {
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();
  const childRef = useRef<any>(null);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formParams, setFormParams] = useState<any>({});

  const [formTable] = useForm();
  // const [isAdd, setIsAdd] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<any>({});
  const [indexHandle, setIndexHandle] = useState<number>(-1);
  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const { user } = useUser();
  const defaultFilter = useDefaultFilterReward();

  const mutateGetGrossProfit = useMutation({
    mutationFn: GrossProfitApis.getGrossProfit,
    onSuccess: (res, variables) => {
      const result = res?.data?.getCompoundInterest || {};
      // console.log(res);
      if (variables.type === 'export') {
        exportExcel_v2(variables.headerRow, res.items || [], 'Lãi gộp');
      } else {
        setDataTable(result);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: mutateUpdateCompoundInterest,
    isPending: isPendingUpdateCompoundInterest,
  } = useMutation({
    mutationFn: GrossProfitApis.updateCompoundInterest,
    onSuccess: () => {
      toastSuccess('Cập nhật lãi gộp thành công');
      handleCancel();
      refetchGetGrossProFit();
    },
    onError: () => {
      toastError('Cập nhật lãi gộp thất bại');
    },
  });

  const refetchGetGrossProFit = useCallback(
    async (isExport?: boolean, headerRow?: any) => {
      try {
        await mutateGetGrossProfit.mutateAsync({
          page: isExport ? 1 : page,
          pageSize: isExport
            ? dataTable.pageInfo?.totalItems || 1000
            : page_size,
          ...(defaultFilter && {
            ...defaultFilter,
            plantId: defaultFilter?.plantId
              ? defaultFilter?.plantId.replace('all', '')
              : undefined,
          }),
          ...(formParams && formParams),
          ...(isExport && {
            headerRow,
            type: 'export',
          }),
        });
      } catch (error) {
        console.log('mutateGetGrossProfit', error);
      }
    },
    [page, page_size, defaultFilter, formParams],
  );

  useEffect(() => {
    refetchGetGrossProFit();
  }, [refetchGetGrossProFit]);

  useEffect(() => {
    setTitle('Lãi gộp');
    setBreadcrumb([{ title: 'Doanh thu/ Thưởng' }, { title: 'Lãi gộp' }]);
  }, [setTitle, setBreadcrumb]);

  const handleExportExcel = () => {
    if (childRef.current) {
      childRef.current.exportExcel();
    }
  };

  const handleImport = () => {
    // console.log('import');
    setIsOpenImportModal(true);
  };

  const handleOkFilter = useCallback(
    (values: any) => {
      navigate(`?page=1&page_size=${page_size}`);
      setFormParams({
        ...values,
        plantId: values?.plantId
          ? values?.plantId.replace('all', '')
          : undefined,
      });
    },
    [navigate, page_size],
  );

  const handleCancelFilter = useCallback(() => {
    console.log('Cancel Filter');
    setFormParams({});
  }, []);

  const handleCancel = () => {
    // setIsAdd(false);
    setIndexHandle(-1);
    formTable.resetFields();
  };

  const handleCancelTable = () => {
    // if (isAdd) {
    //   setDataTable((pre: any) => ({
    //     ...pre,
    //     items: (pre?.items || []).slice(1),
    //   }));
    // }
    handleCancel();
  };

  const validateForm = (value: any) => {
    if (!value.percentProfit) return toastError('Nhập TSLN');

    return true;
  };

  const handleSaveTable = () => {
    if (!validateForm(formTable.getFieldsValue())) return;

    const data = {
      ...dataTable.items[indexHandle],
      percentProfit: formTable.getFieldsValue().percentProfit,
    };

    mutateUpdateCompoundInterest({
      percentProfit: data.percentProfit,
      id: data.id,
      plantId: data.plantId,
      type: data.type,
      age: data.age,
      mrp: data.mrp,
    });
  };

  const handleEditRow = (index: number) => {
    setIndexHandle(index);
    // setIsAdd(false);
    const row = dataTable.items[index];
    formTable.setFieldsValue(row);
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
            disabled={
              // isAdd ||
              indexHandle !== -1
            }
          />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            disabled={
              // isAdd ||
              indexHandle !== -1
            }
          />
        </Col>
        <Col>
          <ButtonImport
            onClick={handleImport}
            disabled={
              // isAdd ||
              indexHandle !== -1 || !(user?.isAdmin || user?.isHO || user?.isNS)
            }
          />
        </Col>
      </Row>
      <FilterGrossProfit
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />

      <TableGrossProfit
        ref={childRef}
        form={formTable}
        data={dataTable}
        // isAdd={isAdd}
        // setIsAdd={setIsAdd}
        indexHandle={indexHandle}
        // setIndexHandle={setIndexHandle}
        onCancel={handleCancelTable}
        onEdit={handleEditRow}
        onSave={handleSaveTable}
        loading={
          mutateGetGrossProfit.isPending || isPendingUpdateCompoundInterest
        }
        refetch={refetchGetGrossProFit}
      />

      <UploadModal
        importId={IMPORT_GROSS_PROFIT}
        templateFileName="Lãi gộp"
        open={isOpenImportModal}
        title={'Thêm mới lãi gộp'}
        setOpen={setIsOpenImportModal}
        refetch={refetchGetGrossProFit}
      />
    </>
  );
};

export default GrossProfitPage;
